import { useNavigate } from 'react-router-dom';
import { useTeacherStats, useTeacherCourses } from '../../lib/hooks';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  BookOpen, 
  Users, 
  Calendar,
  Clock,
  ArrowRight,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const levelColors = {
  A1: 'bg-slate-200 text-slate-800',
  A2: 'bg-blue-100 text-blue-700',
  B1: 'bg-yellow-100 text-yellow-700',
  B2: 'bg-orange-100 text-orange-700',
  C1: 'bg-green-100 text-green-700',
  C2: 'bg-emerald-100 text-emerald-700',
};

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { user } = useAuth();
  const { data: stats, isLoading: statsLoading } = useTeacherStats();
  const { data: courses, isLoading: coursesLoading } = useTeacherCourses();

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isLoading = statsLoading || coursesLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="teacher-dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">
            Bonjour, {user?.first_name} !
          </h1>
          <p className="text-muted-foreground mt-1">
            Voici un aperçu de votre activité
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('teacher_dash_active_courses', lang)}
            </CardTitle>
            <BookOpen className="w-5 h-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-heading">{stats?.active_courses || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Étudiants total
            </CardTitle>
            <Users className="w-5 h-5 text-secondary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-heading">{stats?.total_students || 0}</div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('teacher_dash_sessions_week', lang)}
            </CardTitle>
            <Calendar className="w-5 h-5 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-heading">{stats?.sessions_this_week || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Session Card */}
      {stats?.upcoming_session && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-primary/5 to-secondary/5">
          <CardHeader>
            <CardTitle className="font-heading text-lg">{t('teacher_dash_next_session', lang)}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Calendar className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-lg">{stats.upcoming_session.title}</p>
                  <p className="text-muted-foreground">{stats.upcoming_session.course_name}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" />
                    {formatDateTime(stats.upcoming_session.date)}
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate(`/courses/${stats.upcoming_session.course_id || ''}/session/${stats.upcoming_session.id}`)}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Saisir les présences
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Courses */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-semibold">{t('teacher_dash_my_courses', lang)}</h2>
          <Button variant="ghost" onClick={() => navigate('/courses')}>
            Voir tous
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {courses?.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <BookOpen className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucun cours assigné</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses?.slice(0, 6).map((course) => (
              <Card 
                key={course.id} 
                className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/courses/${course.id}`)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="font-heading text-lg line-clamp-1">
                      {course.name}
                    </CardTitle>
                    <Badge className={levelColors[course.level]}>{course.level}</Badge>
                  </div>
                  {course.description && (
                    <CardDescription className="line-clamp-2">
                      {course.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {course.enrolled_count} étudiants
                    </span>
                    {course.schedule && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {course.schedule}
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Progression</span>
                      <span className="font-medium">
                        {course.completed_sessions} / {course.sessions_count}
                      </span>
                    </div>
                    <Progress 
                      value={course.sessions_count > 0 ? (course.completed_sessions / course.sessions_count) * 100 : 0} 
                      className="h-2" 
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherDashboard;
